import { useEffect } from "react";
import type { ReactNode } from "react";
import { Link } from "react-router-dom";
import { Bot, Lock, Menu, MessageCircle, Users } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardTitle,
} from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { clearSessionData } from "@/lib/utils";

interface FeatureCardProps {
  icon: ReactNode;
  title: string;
  description: string;
}

const features: FeatureCardProps[] = [
  {
    icon: <Bot className="h-6 w-6" />,
    title: "AI Companions",
    description:
      "Your personal AI companions, available 24/7 on the road.",
  },
  {
    icon: <Lock className="h-6 w-6" />,
    title: "Exclusive Content",
    description:
      "Unlock premium drops and subscriber-only content from creators.",
  },
  {
    icon: <MessageCircle className="h-6 w-6" />,
    title: "Private Messaging",
    description: "Real conversations. Private by default.",
  },
  {
    icon: <Users className="h-6 w-6" />,
    title: "Road Community",
    description:
      "Connect with a vetted community who live the lifestyle.",
  },
];

function FeatureCard({ icon, title, description }: FeatureCardProps) {
  return (
    <Card className="jr-card h-full border-[color:var(--jr-border)]">
      <CardContent className="flex h-full flex-col gap-4 p-6">
        <div
          className="flex h-12 w-12 items-center justify-center rounded-2xl border"
          style={{
            background:
              "linear-gradient(180deg, rgba(123,42,36,0.28), rgba(45,14,14,0.92))",
            borderColor: "rgba(227,189,166,0.14)",
            color: "var(--jr-accent)",
          }}
        >
          {icon}
        </div>
        <div className="space-y-2">
          <CardTitle className="text-xl font-semibold text-[color:var(--jr-text)]">
            {title}
          </CardTitle>
          <CardDescription className="text-sm leading-6 text-[color:var(--jr-text-soft)]">
            {description}
          </CardDescription>
        </div>
      </CardContent>
    </Card>
  );
}

export default function HomePage() {
  useEffect(() => {
    clearSessionData();
  }, []);

  return (
    <div className="jr-app-shell">
      <header className="jr-shell-header">
        <div className="jr-screen-pad mx-auto flex w-full max-w-7xl items-center justify-between py-4">
          <div className="flex items-center gap-3">
            <span className="jr-brand-mark h-8 w-8" aria-hidden="true">
              <span className="jr-brand-mark-core" />
            </span>
            <div>
              <p className="jr-display text-lg font-semibold tracking-[0.14em] text-[color:var(--jr-text)] lowercase">
                joyride
              </p>
              <p className="jr-kicker mt-1">Private road companion</p>
            </div>
          </div>
          <Button
            variant="ghost"
            size="icon"
            className="text-[color:var(--jr-text)] hover:bg-[rgba(227,189,166,0.08)] hover:text-[color:var(--jr-text)] md:hidden"
            aria-label="Open menu"
          >
            <Menu className="h-6 w-6" />
          </Button>
          <nav className="hidden items-center gap-3 md:flex">
            <a
              href="#features"
              className="jr-pill bg-transparent text-[color:var(--jr-text-soft)] transition-colors hover:text-[color:var(--jr-text)]"
            >
              Features
            </a>
            <a
              href="#about"
              className="jr-pill bg-transparent text-[color:var(--jr-text-soft)] transition-colors hover:text-[color:var(--jr-text)]"
            >
              Membership
            </a>
            <Button asChild className="jr-action-primary h-auto min-h-11 px-5">
              <Link to="/auth">Join the Ride</Link>
            </Button>
          </nav>
        </div>
      </header>

      <main className="flex-1">
        <section className="jr-screen-pad relative overflow-hidden py-16 sm:py-24">
          <div
            className="pointer-events-none absolute inset-0"
            style={{
              background:
                "radial-gradient(circle at 20% 10%, rgba(123,42,36,0.22), transparent 28%), radial-gradient(circle at 80% 0%, rgba(227,189,166,0.08), transparent 24%)",
            }}
          />
          <div className="mx-auto grid w-full max-w-7xl gap-10 lg:grid-cols-[1.05fr_0.95fr] lg:items-center">
            <div className="relative max-w-2xl">
              <div className="jr-pill mb-5">Exclusive Community</div>
              <h1 className="jr-display text-5xl font-semibold leading-[0.94] text-[color:var(--jr-text)] sm:text-6xl lg:text-7xl">
                Where Every Mile Has a Story.
              </h1>
              <p className="mt-6 max-w-xl text-base leading-7 text-[color:var(--jr-text-soft)] sm:text-lg">
                JoyRide is your private road companion — real connections,
                exclusive content, and an AI companion that actually gets you.
              </p>
              <div className="mt-8 flex flex-col gap-3 sm:flex-row">
                <Button
                  asChild
                  className="jr-action-primary h-auto min-h-12 px-6 text-base"
                >
                  <Link to="/auth">Join the Ride</Link>
                </Button>
                <a
                  href="#features"
                  className="jr-action-secondary inline-flex min-h-12 items-center justify-center px-6 text-base"
                >
                  See What Awaits
                </a>
              </div>
              <div className="mt-8 flex flex-wrap gap-2">
                <span className="jr-pill">Private by default</span>
                <span className="jr-pill">Curated access</span>
                <span className="jr-pill">Luxury after dark</span>
              </div>
            </div>

            <div className="relative">
              <div className="jr-card overflow-hidden border-[color:var(--jr-border-strong)] p-3">
                <div
                  className="rounded-[14px] border p-6 sm:p-7"
                  style={{
                    background:
                      "linear-gradient(180deg, rgba(34,13,13,0.94), rgba(17,7,7,0.98))",
                    borderColor: "rgba(227,189,166,0.1)",
                  }}
                >
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="jr-kicker mb-2">Members only</p>
                      <p className="jr-display text-3xl font-semibold leading-tight text-[color:var(--jr-text)]">
                        Built for the long haul. Made for the night.
                      </p>
                    </div>
                    <span className="jr-badge-count">VIP</span>
                  </div>
                  <div className="mt-8 grid gap-4">
                    <div className="jr-card-muted p-4">
                      <p className="jr-kicker mb-2">Companion mode</p>
                      <p className="text-sm leading-6 text-[color:var(--jr-text-soft)]">
                        Soft-spoken company for the quiet hours, with memory,
                        chemistry, and the kind of conversation that lingers.
                      </p>
                    </div>
                    <div className="jr-card-muted p-4">
                      <p className="jr-kicker mb-2">Subscriber drops</p>
                      <p className="text-sm leading-6 text-[color:var(--jr-text-soft)]">
                        Intimate content, early access, and premium rooms opened
                        only to the right members.
                      </p>
                    </div>
                    <div
                      className="rounded-[14px] border p-4"
                      style={{
                        background:
                          "linear-gradient(180deg, rgba(123,42,36,0.22), rgba(49,14,14,0.85))",
                        borderColor: "rgba(227,189,166,0.16)",
                      }}
                    >
                      <p className="jr-kicker mb-2">Tonight&apos;s mood</p>
                      <p className="text-sm leading-6 text-[color:var(--jr-text)]">
                        Velvet lighting. Locked messages. The road stretching
                        out while somebody worth talking to waits on the other
                        side.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section id="features" className="jr-screen-pad py-20">
          <div className="mx-auto w-full max-w-7xl">
            <div className="mb-10 max-w-2xl">
              <p className="jr-kicker mb-3">Inside the club</p>
              <h2 className="jr-section-title text-[2.4rem] sm:text-[3rem]">
                The vibe comes first. The features follow.
              </h2>
              <p className="mt-4 text-base leading-7 text-[color:var(--jr-text-soft)]">
                JoyRide is designed like an invitation-only lounge for life on
                the road: discreet, aspirational, and tuned for companionship
                after dark.
              </p>
            </div>

            <div className="grid grid-cols-1 gap-6 md:grid-cols-2 xl:grid-cols-4">
              {features.map((feature) => (
                <FeatureCard
                  key={feature.title}
                  icon={feature.icon}
                  title={feature.title}
                  description={feature.description}
                />
              ))}
            </div>
          </div>
        </section>

        <section id="about" className="jr-screen-pad py-20">
          <div className="mx-auto grid w-full max-w-7xl gap-8 lg:grid-cols-[0.92fr_1.08fr]">
            <div className="jr-card p-4">
              <div
                className="flex h-full min-h-[360px] flex-col justify-between rounded-[14px] border p-6"
                style={{
                  background:
                    "linear-gradient(180deg, rgba(28,11,11,0.7), rgba(14,7,7,0.96))",
                  borderColor: "rgba(227,189,166,0.08)",
                }}
              >
                <div>
                  <p className="jr-kicker mb-3">A private invitation</p>
                  <h3 className="jr-display text-4xl font-semibold leading-tight text-[color:var(--jr-text)]">
                    Made for those who know how to live.
                  </h3>
                </div>
                <div className="space-y-3">
                  <div className="jr-card-muted p-4">
                    <p className="text-sm leading-6 text-[color:var(--jr-text-soft)]">
                      JoyRide turns empty miles into something charged:
                      connection, desire, late-night conversation, and a sense
                      that the road still has surprises left.
                    </p>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    <span className="jr-pill">Vetted members</span>
                    <span className="jr-pill">Creator access</span>
                    <span className="jr-pill">After-hours energy</span>
                  </div>
                </div>
              </div>
            </div>

            <div className="flex flex-col justify-center">
              <p className="jr-kicker mb-3">Membership tone</p>
              <h2 className="jr-section-title text-[2.4rem] sm:text-[3rem]">
                Premium intimacy for people who live between destinations.
              </h2>
              <p className="mt-5 text-base leading-7 text-[color:var(--jr-text-soft)]">
                This is not another broad social feed. JoyRide is a members'
                room for people who want conversation with edge, content with
                scarcity, and companionship that feels personal instead of
                automated.
              </p>
              <p className="mt-4 text-base leading-7 text-[color:var(--jr-text-soft)]">
                From private messaging to AI companions and premium creator
                access, everything is built to feel curated, discreet, and a
                little indulgent.
              </p>

              <div className="mt-8 grid gap-4 sm:grid-cols-2">
                <div className="jr-card-muted p-4">
                  <p className="jr-kicker mb-2">Social proof</p>
                  <p className="text-sm leading-6 text-[color:var(--jr-text-soft)]">
                    Join 50,000+ members on the road.
                  </p>
                </div>
                <div className="jr-card-muted p-4">
                  <p className="jr-kicker mb-2">Access</p>
                  <p className="text-sm leading-6 text-[color:var(--jr-text-soft)]">
                    Enter a vetted network where conversation and content carry
                    real value.
                  </p>
                </div>
              </div>

              <div className="mt-8">
                <Button
                  asChild
                  className="jr-action-secondary h-auto min-h-12 px-6 text-base"
                >
                  <Link to="/auth">See What Awaits</Link>
                </Button>
              </div>
            </div>
          </div>
        </section>

        <section className="jr-screen-pad pb-20">
          <div className="jr-card mx-auto w-full max-w-5xl p-6 sm:p-8">
            <div className="flex flex-col gap-6 md:flex-row md:items-center md:justify-between">
              <div className="max-w-3xl">
                <p className="jr-kicker mb-4">After dark</p>
                <p className="jr-display text-3xl font-semibold leading-tight text-[color:var(--jr-text)] sm:text-4xl">
                  Private rooms, exclusive drops, and company that stays up as
                  late as you do.
                </p>
                <p className="mt-4 text-sm leading-6 text-[color:var(--jr-text-soft)]">
                  Membership unlocks the part of the platform built for the
                  quiet hours, the dim glow of the dash, and the miles nobody
                  else sees.
                </p>
              </div>
              <Button asChild className="jr-action-primary h-auto min-h-12 px-6">
                <Link to="/auth">Join the Ride</Link>
              </Button>
            </div>
          </div>
        </section>
      </main>

      <footer
        className="jr-screen-pad py-12"
        style={{
          background:
            "linear-gradient(180deg, rgba(18,8,8,0.96), rgba(12,7,7,1))",
          borderTop: "1px solid var(--jr-border)",
        }}
      >
        <div className="mx-auto w-full max-w-7xl">
          <div className="grid grid-cols-1 gap-10 md:grid-cols-4">
            <div>
              <div className="mb-4 flex items-center gap-3">
                <span className="jr-brand-mark h-8 w-8" aria-hidden="true">
                  <span className="jr-brand-mark-core" />
                </span>
                <h3 className="jr-display text-2xl font-semibold lowercase text-[color:var(--jr-text)]">
                  joyride
                </h3>
              </div>
              <p className="max-w-xs text-sm leading-6 text-[color:var(--jr-text-soft)]">
                A luxury noir membership experience for connection, exclusive
                content, and companionship on the road.
              </p>
            </div>

            <div>
              <h4 className="mb-4 text-sm font-semibold uppercase tracking-[0.18em] text-[color:var(--jr-text)]">
                Company
              </h4>
              <ul className="space-y-2 text-sm text-[color:var(--jr-text-soft)]">
                <li>
                  <Link
                    to="/about"
                    className="transition-colors hover:text-[color:var(--jr-accent)]"
                  >
                    About Us
                  </Link>
                </li>
                <li>
                  <Link
                    to="/careers"
                    className="transition-colors hover:text-[color:var(--jr-accent)]"
                  >
                    Careers
                  </Link>
                </li>
                <li>
                  <Link
                    to="/contact"
                    className="transition-colors hover:text-[color:var(--jr-accent)]"
                  >
                    Contact
                  </Link>
                </li>
              </ul>
            </div>

            <div>
              <h4 className="mb-4 text-sm font-semibold uppercase tracking-[0.18em] text-[color:var(--jr-text)]">
                Membership
              </h4>
              <ul className="space-y-2 text-sm text-[color:var(--jr-text-soft)]">
                <li>
                  <Link
                    to="/guides"
                    className="transition-colors hover:text-[color:var(--jr-accent)]"
                  >
                    Member Guide
                  </Link>
                </li>
                <li>
                  <Link
                    to="/blog"
                    className="transition-colors hover:text-[color:var(--jr-accent)]"
                  >
                    Creator Journal
                  </Link>
                </li>
                <li>
                  <Link
                    to="/webinars"
                    className="transition-colors hover:text-[color:var(--jr-accent)]"
                  >
                    Private Events
                  </Link>
                </li>
              </ul>
            </div>

            <div>
              <h4 className="mb-4 text-sm font-semibold uppercase tracking-[0.18em] text-[color:var(--jr-text)]">
                Join the ride
              </h4>
              <p className="mb-4 text-sm leading-6 text-[color:var(--jr-text-soft)]">
                Enter the room, meet the community, and see what opens up after
                dark.
              </p>
              <Button asChild className="jr-action-primary h-auto min-h-11 w-full">
                <Link to="/auth">Join the Ride</Link>
              </Button>
            </div>
          </div>

          <Separator className="my-8 bg-[color:var(--jr-border)]" />

          <div className="flex flex-col items-start justify-between gap-4 md:flex-row md:items-center">
            <p className="text-sm text-[color:var(--jr-text-muted)]">
              &copy; {new Date().getFullYear()} JoyRide. The night has company.
            </p>
            <div className="flex items-center gap-4">
              <a
                href="https://facebook.com/joyride"
                aria-label="Facebook"
                className="text-[color:var(--jr-text-muted)] transition-colors hover:text-[color:var(--jr-accent)]"
              >
                <span className="sr-only">Facebook</span>
                <svg
                  className="h-6 w-6"
                  fill="currentColor"
                  viewBox="0 0 24 24"
                  aria-hidden="true"
                  role="img"
                >
                  <path
                    fillRule="evenodd"
                    d="M22 12c0-5.523-4.477-10-10-10S2 6.477 2 12c0 4.991 3.657 9.128 8.438 9.878v-6.987h-2.54V12h2.54V9.797c0-2.506 1.492-3.89 3.777-3.89 1.094 0 2.238.195 2.238.195v2.46h-1.26c-1.243 0-1.63.771-1.63 1.562V12h2.773l-.443 2.89h-2.33v6.988C18.343 21.128 22 16.991 22 12z"
                    clipRule="evenodd"
                  />
                </svg>
              </a>
              <a
                href="https://twitter.com/joyride"
                aria-label="Twitter"
                className="text-[color:var(--jr-text-muted)] transition-colors hover:text-[color:var(--jr-accent)]"
              >
                <span className="sr-only">Twitter</span>
                <svg
                  className="h-6 w-6"
                  fill="currentColor"
                  viewBox="0 0 24 24"
                  aria-hidden="true"
                  role="img"
                >
                  <path d="M8.29 20.251c7.547 0 11.675-6.253 11.675-11.675 0-.178 0-.355-.012-.53A8.348 8.348 0 0022 5.92a8.19 8.19 0 01-2.357.646 4.118 4.118 0 001.804-2.27 8.224 8.224 0 01-2.605.996 4.107 4.107 0 00-6.993 3.743 11.65 11.65 0 01-8.457-4.287 4.106 4.106 0 001.27 5.477A4.072 4.072 0 012.8 9.713v.052a4.105 4.105 0 003.292 4.022 4.095 4.095 0 01-1.853.07 4.108 4.108 0 003.834 2.85A8.233 8.233 0 012 18.407a11.616 11.616 0 006.29 1.84" />
                </svg>
              </a>
              <a
                href="https://instagram.com/joyride"
                aria-label="Instagram"
                className="text-[color:var(--jr-text-muted)] transition-colors hover:text-[color:var(--jr-accent)]"
              >
                <span className="sr-only">Instagram</span>
                <svg
                  className="h-6 w-6"
                  fill="currentColor"
                  viewBox="0 0 24 24"
                  aria-hidden="true"
                  role="img"
                >
                  <path
                    fillRule="evenodd"
                    d="M12.315 2c2.43 0 2.784.013 3.808.06 1.064.049 1.791.218 2.427.465a4.902 4.902 0 011.772 1.153 4.902 4.902 0 011.153 1.772c.247.636.416 1.363.465 2.427.048 1.067.06 1.407.06 4.123v.08c0 2.643-.012 2.987-.06 4.043-.049 1.064-.218 1.791-.465 2.427a4.902 4.902 0 01-1.153 1.772 4.902 4.902 0 01-1.772 1.153c-.636.247-1.363.416-2.427.465-1.067.048-1.407.06-4.123.06h-.08c-2.643 0-2.987-.012-4.043-.06-1.064-.049-1.791-.218-2.427-.465a4.902 4.902 0 01-1.772-1.153 4.902 4.902 0 01-1.153-1.772c-.247-.636-.416-1.363-.465-2.427-.047-1.024-.06-1.379-.06-3.808v-.63c0-2.43.013-2.784.06-3.808.049-1.064.218-1.791.465-2.427a4.902 4.902 0 011.153-1.772A4.902 4.902 0 015.45 2.525c.636-.247 1.363-.416 2.427-.465C8.901 2.013 9.256 2 11.685 2h.63zm-.081 1.802h-.468c-2.456 0-2.784.011-3.807.058-.975.045-1.504.207-1.857.344-.467.182-.8.398-1.15.748-.35.35-.566.683-.748 1.15-.137.353-.3.882-.344 1.857-.047 1.023-.058 1.351-.058 3.807v.468c0 2.456.011 2.784.058 3.807.045.975.207 1.504.344 1.857.182.466.399.8.748 1.15.35.35.683.566 1.15.748.353.137.882.3 1.857.344 1.054.048 1.37.058 4.041.058h.08c2.597 0 2.917-.01 3.96-.058.976-.045 1.505-.207 1.858-.344.466-.182.8-.398 1.15-.748.35-.35.566-.683.748-1.15.137-.353.3-.882.344-1.857.048-1.055.058-1.37.058-4.041v-.08c0-2.597-.01-2.917-.058-3.96-.045-.976-.207-1.505-.344-1.858a3.097 3.097 0 00-.748-1.15 3.098 3.098 0 00-1.15-.748c-.353-.137-.882-.3-1.857-.344-1.023-.047-1.351-.058-3.807-.058zM12 6.865a5.135 5.135 0 110 10.27 5.135 5.135 0 010-10.27zm0 1.802a3.333 3.333 0 100 6.666 3.333 3.333 0 000-6.666zm5.338-3.205a1.2 1.2 0 110 2.4 1.2 1.2 0 010-2.4z"
                    clipRule="evenodd"
                  />
                </svg>
              </a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
